import { useApolloClient } from '@apollo/react-hooks'
import { createStyles, makeStyles } from '@material-ui/core'
import type { Contact, Deal, QuoteQuery, SearchContactDealsQuery } from '@paintscout/api'
import { QuoteDocument as QuoteGQLDocument, SearchContactDealsDocument, useContactQuery } from '@paintscout/api'
import type { DialogProps, PLDDeal } from '@paintscout/ui'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  SelectDealDialog,
  TileList,
  useClientOptions,
  useCrmDialogs,
  useDialogs,
  useUser
} from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import type { QuoteTemplateOption, QuoteTypeOption } from '@paintscout/util/builder'
import {
  createQuote,
  getObjectLabels,
  getOwnerFromUser,
  getQuoteTemplateOptions,
  getQuoteTypeOptions
} from '@paintscout/util/builder'
import { useSnackbar } from 'notistack'
import type { QuoteDocument, QuoteSource } from 'paintscout'
import React, { useState } from 'react'
import * as Sentry from '@sentry/react'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface CreateQuoteDialogProps extends DialogProps {
  forContact?: Contact
  forDeal?: Deal
  invoice?: boolean
  onConfirm?: (quoteDocument: QuoteDocument, templateOption?: QuoteTemplateOption) => void
  onCancel?: () => void
}

export interface CreateQuoteDialogState {
  loading: boolean
  selectedType: string
  hasTemplates: boolean
  selectedTemplate: QuoteTemplateOption
  selectedCustomTemplate: boolean
}

export const useStyles = makeStyles((theme) => {
  return createStyles({
    form: {
      display: 'block'
    },
    dialogTitle: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(2.75),
        paddingRight: theme.spacing(2.75)
      }
    },
    dialogContent: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3)
    },
    tiles: {
      paddingLeft: 0,
      paddingRight: 0
    }
  })
})

function CreateQuoteDialog({
  open,
  invoice,
  onCancel,
  onConfirm,
  forContact,
  forDeal,
  ...baseDialogProps
}: CreateQuoteDialogProps) {
  const classes = useStyles({})
  const { options, rates } = useClientOptions()
  const { user, isSuperadmin, isCorporate, isMasquerading, isSupport, preferences } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { crmContext } = useCrmDialogs()
  const { subtitle } = crmContext || {}

  const { data: forDealContactData } = useContactQuery({
    variables: { id: forDeal?.contact?.id },
    fetchPolicy: 'cache-and-network',
    skip: !!forContact || !forDeal?.contact
  })

  const [type, setType] = useState<QuoteTypeOption>(null)
  const [template, setTemplate] = useState<QuoteTemplateOption>(null)
  const [isCustomTemplate, setIsCustomTemplate] = useState(false)
  const [loading, setLoading] = useState(false)
  const apolloClient = useApolloClient()
  const { canDeactivateSettingsItems } = useFlags()
  const quoteTypeOptions = getQuoteTypeOptions({ options, canDeactivate: !!canDeactivateSettingsItems })
  const objectLabels = getObjectLabels({ options, invoice })

  const quoteTemplateOptions = type
    ? getQuoteTemplateOptions({
        options,
        quoteType: type.key,
        canDeactivate: !!canDeactivateSettingsItems
      })
    : []

  const hasTemplates = !invoice && quoteTemplateOptions?.length > 0
  const useTemplatesOnly = options.options.quotes.useTemplatesOnly

  async function handleQuoteTemplateChange(event: any, key: string, custom: boolean) {
    if (custom) {
      setIsCustomTemplate(true)
      await handleCreateQuote(type, null)
    } else {
      const selectedTemplate = quoteTemplateOptions.find((i) => i.key === key)

      setTemplate(selectedTemplate)

      if (selectedTemplate) {
        await handleCreateQuote(type, selectedTemplate)
      }
    }
  }

  async function handleQuoteTypeChange(event: any, key: string) {
    const selectedType = quoteTypeOptions.find((i) => i.key === key)

    const templates =
      !invoice && selectedType
        ? getQuoteTemplateOptions({
            options,
            quoteType: selectedType.key,
            canDeactivate: !!canDeactivateSettingsItems
          })
        : []

    // if there are no templates for a new quote, or we're creating a template
    if (templates.length === 0 && selectedType) {
      await handleCreateQuote(selectedType, null)
    }
    setType(selectedType)
  }

  async function handleCreateQuote(quoteType: QuoteTypeOption, quoteTemplateOption: QuoteTemplateOption) {
    try {
      const { signature, defaultEstimatorNotes } = preferences
      setLoading(true)

      const quoteTemplate = quoteTemplateOption
        ? await apolloClient
            .query<QuoteQuery>({
              query: QuoteGQLDocument,
              variables: { id: quoteTemplateOption.key }
            })
            .then((res) => res.data?.quote)
        : null

      const owner = isSuperadmin || isSupport || (isCorporate && isMasquerading) ? null : getOwnerFromUser(user)
      const newQuoteContact = forContact ? forContact : forDealContactData?.contact ? forDealContactData.contact : null
      if (forContact?.source === 'pipeline_deals' && forContact?.source_id) {
        const contactDeals: PLDDeal[] = await apolloClient
          .query<SearchContactDealsQuery>({
            query: SearchContactDealsDocument,
            variables: { contactSourceId: newQuoteContact.source_id }
          })
          .then((res) => res.data?.searchContactDeals?.results ?? [])

        const dealId = contactDeals?.length ? await handleSelectDeal(contactDeals, newQuoteContact) : null
        const source: QuoteSource = dealId
          ? {
              dealId: Number(dealId)
            }
          : null
        const quote = createQuote({
          contact: newQuoteContact,
          deal: forDeal,
          ...(source ? { source } : {}),
          options,
          owner,
          estimatorNotes: defaultEstimatorNotes,
          signature,
          quoteType: quoteType.key,
          template: quoteTemplateOption
            ? {
                ...quoteTemplateOption,
                content: quoteTemplate
              }
            : null,
          invoice,
          rates,
          type: 'quote'
        })

        onConfirm(quote)
        setLoading(false)
      } else {
        const quote = createQuote({
          contact: newQuoteContact,
          deal: forDeal,
          options,
          owner,
          estimatorNotes: defaultEstimatorNotes,
          signature,
          quoteType: quoteType.key,
          template: quoteTemplateOption
            ? {
                ...quoteTemplateOption,
                content: quoteTemplate
              }
            : null,
          invoice,
          rates,
          type: 'quote'
        })

        setLoading(false)

        onConfirm(quote)
      }
    } catch (error) {
      Sentry.captureException(error)
      setLoading(false)
      console.log({ error })
      enqueueSnackbar(`Unable to create ${objectLabels.quote.value.toLowerCase()}`, { variant: 'error' })
    }
  }

  const handleSelectDeal = async (deals: PLDDeal[], contact: Contact): Promise<string> => {
    return new Promise(async (resolve) => {
      await openDialog(
        SelectDealDialog,
        {
          deals,
          contact,
          onConfirm: (dealId: string) => {
            dismissDialog()
            if (dealId !== 'none') {
              resolve(dealId)
            } else {
              resolve(null)
            }
          },
          onCancel: () => {
            dismissAllDialogs()
          }
        },
        { replace: false }
      )
    })
  }

  const handleBack = () => {
    setType(null)
    setTemplate(null)
    setIsCustomTemplate(false)
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth={'md'} fullWidth={true} {...baseDialogProps}>
      <DialogTitle
        loading={loading}
        subtitle={subtitle}
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={onCancel} disabled={loading}>
            <CloseIcon />
          </IconButton>
        }
      >
        {invoice ? 'Create New Invoice' : `Create New ${objectLabels.quote.value}`}
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <form className={classes.form}>
          {hasTemplates && type && (
            <TileList
              classes={{ tiles: classes.tiles }}
              title={`${objectLabels.quote.value} Template`}
              items={quoteTemplateOptions}
              disabled={loading}
              selected={template?.key}
              onSelect={(ev, key) => handleQuoteTemplateChange(ev, key, false)}
              onCustom={(ev) => handleQuoteTemplateChange(ev, null, true)}
              customTile={
                useTemplatesOnly
                  ? false
                  : {
                      title: 'Empty Quote',
                      selected: isCustomTemplate
                    }
              }
              showUnselected
            />
          )}
          <TileList
            classes={{ tiles: classes.tiles }}
            title={`${objectLabels.quote.value} Type`}
            disabled={loading}
            subtitle={`Rates, Line Items, and Areas are filtered by ${objectLabels.quote.value.toLowerCase()} type`}
            items={quoteTypeOptions}
            selected={type?.key}
            onSelect={handleQuoteTypeChange}
            showUnselected={!type}
            variant="standard"
          />
        </form>
      </DialogContent>
      {type && (
        <DialogActions
          leftButton={
            <Button variant="textNoPadding" onClick={handleBack} disabled={loading}>
              Back
            </Button>
          }
        />
      )}
    </Dialog>
  )
}

export default CreateQuoteDialog
